import type { MButtonProps } from '@mts/components/Button';
import type { TableActionType } from '@mts/components/Table/src/types/table';
import type { FieldMapping } from '@mts/render/types';
import type { NamePath, RuleObject } from 'ant-design-vue/es/form/interface';
import type { RowProps } from 'ant-design-vue/es/grid/Row';
import type { CSSProperties, VNode } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { ModalProps } from '@mts/components/Modal';
import type { SysConfigValue } from '@/api/ims-system';
import type { FormFocusReturn } from '../hooks/useFormFocus';
import type { ColEx, ComponentProps, ComponentType } from '.';
import type { FormItem } from './formItem';

export type FieldMapToTime = [string, [string, string], (string | [string, string])?][];

export type Rule = RuleObject & {
  trigger?: 'blur' | 'change' | ['change', 'blur'];
};

export interface RenderCallbackParams {
  schema: FormSchemaInner;
  values: Recordable;
  model: Recordable;
  field: string;
  route?: RouteLocationNormalizedLoaded;
  sysConfig?: Recordable<SysConfigValue>;
}

export interface ButtonProps extends MButtonProps {
  text?: string;
}

export interface SetFieldsValueOptions {
  /** 自动触发关联控件的更新（首次赋值） */
  autoRelate?: boolean;
  /** 赋值前先清空 formModel（所有字段置为 undefined） */
  clearBeforeSet?: boolean;
}

export interface FormActionType {
  $el?: HTMLDivElement;
  submit: (e?: Event) => Promise<void>;
  setFieldsValue: (values: Recordable, opt?: SetFieldsValueOptions) => Promise<void>;
  resetFields: (noSubmit?: boolean | PointerEvent) => Promise<void>;
  getFieldsValue: <T = Recordable>() => T;
  clearValidate: (name?: string | string[]) => Promise<void>;
  updateRelateFields: (field: string, value: any, schema?: FormSchema) => void;
  updateSchema: (data: Partial<FormSchemaInner> | Partial<FormSchemaInner>[]) => Promise<void>;
  resetSchema: (data: Partial<FormSchemaInner> | Partial<FormSchemaInner>[]) => Promise<void>;
  setProps: (formProps: Partial<FormProps>) => Promise<void>;
  removeSchemaByField: (field: string | string[]) => Promise<void>;
  appendSchemaByField: (
    schema: FormSchemaInner | FormSchemaInner[],
    prefixField: string | undefined,
    first?: boolean | undefined,
  ) => Promise<void>;
  validateFields: (nameList?: NamePath[]) => Promise<any>;
  validate: <T = Recordable>(nameList?: NamePath[] | false) => Promise<T>;
  scrollToField: (name: NamePath, options?: ScrollOptions) => Promise<void>;
  focusByField: FormFocusReturn['focusByField'];
  resetDefaultField: (name?: NamePath[]) => void;
  /** 重渲染字段组件，默认需指定字段的 { "canRefresh": true } */
  refreshSchemas: (fields?: string[]) => void;
}

export interface ModelMappingRule {
  subFields: string[];
  /** 拼接符号，默认为空格 ' ' */
  separator: string;
}

export interface FormProps {
  name?: string;
  readonlyMode?: boolean;
  /** @deprecated */
  inlineTextSplit?: boolean;
  layout?: 'vertical' | 'inline' | 'horizontal';
  // Form value
  model?: Recordable;
  modelMapping?: Recordable<ModelMappingRule>;
  // The width of all items in the entire form
  labelWidth?: number | string;
  // Label 文本超出宽度时显示省略号 + Tooltip
  labelEllipsis?: boolean;
  // alignment
  labelAlign?: 'left' | 'right';
  // Row configuration for the entire form
  rowProps?: RowProps;
  // Submit form on reset
  submitOnReset?: boolean;
  // Col configuration for the entire form
  labelCol?: Partial<ColEx>;
  // Col configuration for the entire form
  wrapperCol?: Partial<ColEx>;

  // General row style
  baseRowStyle?: CSSProperties;

  // General col configuration
  baseColProps?: Partial<ColEx>;

  // Form configuration rules
  schemas?: FormSchema[];
  // Function values used to merge into dynamic control form items
  mergeDynamicData?: Recordable;
  inset?: boolean;
  // Compact mode for search forms
  compact?: boolean;
  // Blank line span
  emptySpan?: number | Partial<ColEx>;
  // Internal component size of the form
  size?: 'default' | 'middle' | 'small' | 'large';
  // Whether to disable
  disabled?: boolean;
  // Whether to readonly
  readonly?: boolean;
  // Time interval fields are mapped into multiple
  fieldMapToTime?: FieldMapToTime;
  strongRelate?: boolean;
  hideLabel?: boolean;
  // Placeholder is set automatically
  autoSetPlaceHolder?: boolean;
  // Auto submit on press enter on input
  autoSubmitOnEnter?: boolean;
  // Auto focus next form item on press enter
  enterFocus?: boolean;
  // Auto focus next form item on scan code
  scanFocusNext?: boolean;
  // Check whether the information is added to the label
  rulesMessageJoinLabel?: boolean;
  // Whether to show collapse and expand buttons
  showAdvancedButton?: boolean;
  // Automatically collapse over the specified number of rows
  autoAdvancedLine?: number;
  // Always show lines
  alwaysShowLines?: number;
  // Whether to show the operation button
  showActionButtonGroup?: boolean;

  // Reset button configuration
  resetButtonOptions?: Partial<ButtonProps>;

  // Confirm button configuration
  submitButtonOptions?: Partial<ButtonProps>;

  // Show more button
  showMoreButton?: boolean;
  // More button configuration
  moreButtonOptions?: Partial<ButtonProps>;
  // More modal configuration
  moreModalProps?: Partial<ModalProps>;

  // Operation column configuration
  actionColOptions?: Partial<ColEx>;

  // Show reset button
  showResetButton?: boolean;
  // Show confirmation button
  showSubmitButton?: boolean;

  resetFunc?: () => Promise<void>;
  submitFunc?: () => Promise<void>;
  transformDateFunc?: (date: any, format?: string) => string;
  colon?: boolean;
  /** 区域级每行列数（来自低代码 columnSpan 配置），PopoverSearch 用于计算等宽栅格 */
  columnSpan?: number;
}
export interface RenderOpts {
  disabled: boolean;
  [key: string]: any;
}

export interface BaseFormSchema<T extends ComponentType = any> {
  // Field name
  field: string;
  // Extra Fields name[]
  fields?: string[];
  fieldmapping?: FieldMapping[];
  // Relate Fields name[]
  relateFields?: string[];
  relateFieldsMap?: Recordable<string>;
  // Event name triggered by internal value change, default change
  changeEvent?: string;
  // Variable name bound to v-model Default value
  valueField?: string;
  // Label name
  label?: string | VNode | ((renderCallbackParams: RenderCallbackParams) => string | VNode);
  /** 多语言key标记 */
  dataLangKey?: string;
  // Auxiliary text
  subLabel?: string;
  hideLabel?: boolean;
  // Help text on the right side of the text
  helpMessage?:
    | string
    | string[]
    | ((renderCallbackParams: RenderCallbackParams) => string | string[]);
  // BaseHelp component props
  helpComponentProps?: Partial<HelpComponentProps>;
  // Label width, if it is passed, the labelCol and WrapperCol configured by itemProps will be invalid
  labelWidth?: string | number;
  // Disable the adjustment of labelWidth with global settings of formModel, and manually set labelCol and wrapperCol by yourself
  disabledLabelWidth?: boolean;
  // Component parameters
  componentProps?:
    | ((opt: {
        schema: FormSchema;
        tableAction: TableActionType;
        formActionType: FormActionType;
        formModel: Recordable;
      }) => ComponentProps[T])
    | ComponentProps[T];
  // Required
  required?: boolean | ((renderCallbackParams: RenderCallbackParams) => boolean);

  suffix?:
    | string
    | number
    | VNode
    | ((renderCallbackParams: RenderCallbackParams) => string | VNode | number);
  prefix?:
    | string
    | number
    | VNode
    | ((renderCallbackParams: RenderCallbackParams) => string | VNode | number);
  // Validation rules
  rules?: Rule[];
  // Check whether the information is added to the label
  rulesMessageJoinLabel?: boolean;

  // Reference formModelItem
  itemProps?: Partial<FormItem>;

  // col configuration outside formModelItem
  colProps?: Partial<ColEx>;

  // 默认值
  defaultValue?: any;

  // 额外默认值数组对象
  defaultValueObj?: { [key: string]: any };

  // 是否自动处理与时间相关组件的默认值
  isHandleDateDefaultValue?: boolean;

  // 是否使用valueFormat自动处理默认值
  isHandleDefaultValue?: boolean;

  isAdvanced?: boolean;
  canRefresh?: boolean;

  // 默认仅显示在更多中
  hideInMore?: boolean;
  disablePin?: boolean;
  triggerSearch?: boolean;

  showIn?: string;

  // Matching details components
  span?: number;

  ifShow?: boolean | ((renderCallbackParams: RenderCallbackParams) => boolean);

  show?: boolean | ((renderCallbackParams: RenderCallbackParams) => boolean);

  // 权限编码控制是否显示
  auth?: string | string[];

  // Render the content in the form-item tag
  render?: (
    renderCallbackParams: RenderCallbackParams,
    opts?: RenderOpts,
  ) => VNode | VNode[] | string;

  // Rendering col content requires outer wrapper form-item
  renderColContent?: (
    renderCallbackParams: RenderCallbackParams,
    opts: RenderOpts,
  ) => VNode | VNode[] | string;

  renderComponentContent?:
    | ((renderCallbackParams: RenderCallbackParams, opts: RenderOpts) => any)
    | VNode
    | VNode[]
    | string;

  // Custom slot, similar to renderColContent
  colSlot?: string;

  dynamicDisabled?: boolean | ((renderCallbackParams: RenderCallbackParams) => boolean);

  dynamicReadonly?: boolean | ((renderCallbackParams: RenderCallbackParams) => boolean);

  dynamicRules?: (renderCallbackParams: RenderCallbackParams) => Rule[];

  valueFormat?: (arg: Partial<RenderCallbackParams> & { value: any }) => any;
}
export interface ComponentFormSchema<T extends ComponentType = any> extends BaseFormSchema<T> {
  // render component
  component: T;
}

export interface SlotFormSchema extends BaseFormSchema {
  // Custom slot, in form-item
  slot: string;
}

type ComponentFormSchemaType<T extends ComponentType = ComponentType> = T extends any
  ? ComponentFormSchema<T>
  : never;

export type FormSchema = ComponentFormSchemaType | SlotFormSchema;

export type FormSchemaInner = Partial<ComponentFormSchema> &
  Partial<SlotFormSchema> &
  BaseFormSchema;

export function isSlotFormSchema(schema: FormSchemaInner): schema is SlotFormSchema {
  return 'slot' in schema;
}

export function isComponentFormSchema(schema: FormSchemaInner): schema is ComponentFormSchema {
  return !isSlotFormSchema(schema);
}
export interface HelpComponentProps {
  maxWidth: string;
  // Whether to display the serial number
  showIndex: boolean;
  // Text list
  text: any;
  // colour
  color: string;
  // font size
  fontSize: string;
  icon: string;
  absolute: boolean;
  // Positioning
  position: any;
}
